import {
	invalidateIntegrationsQuery,
	useGetExternalIntegrationsQuery,
	usePostExternalIntegrationEnableMutation,
} from '@Queries/index';
import { baseStyleSheet, bs } from '@Styles/styles';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import clioLogo from '../../../assets/logo_clio.png';
import housecallProLogo from '../../../assets/logo_housecallpro_banner.png';
import microsoftDynamics365SalesLogo from '../../../assets/logo_microsoft_dynamics_sales_banner.png';
import myCaseLogo from '../../../assets/logo_mycase.svg';
import eclipseLogo from '../../../assets/logo_nasasoft.png';
import redtailLogo from '../../../assets/logo_redtail.png';
import salesforceLogo from '../../../assets/logo_salesforce.svg';
import serviceTitanLogo from '../../../assets/logo_servicetitan_banner.png';
import wealthboxLogo from '../../../assets/logo_wealthbox.png';
import practicePantherLogo from '../../../assets/practice-panther-logo.svg';
import qboLogo from '../../../assets/qb-logo-horizontal-preferred.svg';
import { Button } from '../../../components-v2/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { LoadingSpinner } from '../../LoadingSpinner';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { IntegrationCard } from '../IntegrationCard';
import { getProviderDisplayName } from './displayNames';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	impersonationContext?: Api.IImpersonationContext;
	searchQuery?: string;
	styles?: StyleDeclarationValue[];
}

const AvailableExternalIntegrationWithIndustries = [
	{ provider: Api.IntegrationProvider.PracticePanther, industry: Api.Industry.Legal },
	{ provider: Api.IntegrationProvider.MyCase, industry: Api.Industry.Legal },
	{ provider: Api.IntegrationProvider.Eclipse, industry: Api.Industry.Insurance },
	{ provider: Api.IntegrationProvider.Clio, industry: Api.Industry.Legal },
	{ provider: Api.IntegrationProvider.ServiceTitan, industry: Api.Industry.HomeMaintenance },
	{ provider: Api.IntegrationProvider.HousecallPro, industry: Api.Industry.HomeMaintenance },
	{ provider: Api.IntegrationProvider.Wealthbox, industry: Api.Industry.Financial },
	{ provider: Api.IntegrationProvider.Redtail, industry: Api.Industry.Financial },
	{ provider: Api.IntegrationProvider.Bloomerang, industry: Api.Industry.NonProfit },
];

const accountCleanUpModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const needAccountResetModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCancel: true,
		representedObject: false,
		title: 'OK',
	},
];

export const ExternalIntegrations = ({ className, styles, impersonationContext, searchQuery }: IProps) => {
	const normalizedSearch = searchQuery?.trim().toLowerCase() ?? '';
	const matchesSearch = (name: string) => !normalizedSearch || name.toLowerCase().includes(normalizedSearch);
	const match = useRouteMatch();
	const userSession = useAdminUserSession();
	const regularUserSession = useUserSession();
	const errorMessages = useErrorMessages();
	const isAdmin = !!impersonationContext;

	const [isAccountCleanUpModalOpen, setIsAccountCleanUpModalOpen] = React.useState(false);
	const [isNeedResetModalOpen, setIsNeedResetModalOpen] = React.useState(false);
	const [modalIntegrationProvider, setModalIntegrationProvider] = React.useState(undefined);

	const getExternalIntegrationsQuery = useGetExternalIntegrationsQuery({
		impersonationContext,
	});

	const externalIntegrations = React.useMemo(() => {
		if (!getExternalIntegrationsQuery.data) {
			return [];
		}

		const userIndustry = impersonationContext
			? impersonationContext.account?.additionalInfo?.industry
			: userSession.account?.additionalInfo?.industry;

		const alwaysAvailableExternalIntegrations = [
			{ provider: Api.IntegrationProvider.MicrosoftDynamics365Sales },
			{ provider: Api.IntegrationProvider.Salesforce },
			{ provider: Api.IntegrationProvider.QuickBooksOnline },
		];

		// Create a map of all available integrations with their enabled status
		const allIntegrationsWithStatus = AvailableExternalIntegrationWithIndustries.map(availableIntegration => {
			const existingIntegration = getExternalIntegrationsQuery.data?.find(
				i => i.provider === availableIntegration.provider
			);
			return {
				...existingIntegration,
				provider: availableIntegration.provider,
				industry: availableIntegration.industry,
				enabled: existingIntegration ? existingIntegration.enabled : false,
			};
		});

		// Filter integrations: admins see all; non-admins see enabled OR industry-matched
		let availableIntegrations = isAdmin
			? allIntegrationsWithStatus
			: allIntegrationsWithStatus.filter(
					availableIntegration => availableIntegration.enabled || availableIntegration.industry === userIndustry
				);

		let alwaysAvailableIntegrations = alwaysAvailableExternalIntegrations.map(availableIntegration => {
			const existingIntegration = getExternalIntegrationsQuery.data?.find(
				i => i.provider === availableIntegration.provider
			);
			return {
				...existingIntegration,
				provider: availableIntegration.provider,
				enabled: existingIntegration ? existingIntegration.enabled : false,
			};
		});

		if (isAdmin) {
			const hasOnlyForDevelopersPermission = userSession.hasPermission(AdminPermission.OnlyForDevelopers);
			if (!hasOnlyForDevelopersPermission) {
				availableIntegrations = availableIntegrations.filter(availableIntegration => {
					// Eclipse V2: Always show if enabled AND configured.
					// Otherwise, show unless there's an enabled V1 integration.
					if (availableIntegration.provider === Api.IntegrationProvider.Eclipse) {
						const isEnabledAndConfigured =
							availableIntegration.enabled && !availableIntegration.configuration?.needsConfiguration;
						if (isEnabledAndConfigured) {
							return true;
						}
						// Hide if V1 Eclipse is enabled
						const hasEnabledV1Eclipse = impersonationContext
							? impersonationContext.account?.integrations?.eclipse?.enabled
							: userSession.account?.integrations?.eclipse?.enabled;
						return !hasEnabledV1Eclipse;
					}

					// Bloomerang: Only show if enabled on the account. Otherwise, hide from non-developers.
					// Not also gated on needsConfiguration like the V2 cards above: for Bloomerang that stays true
					// until the sync user finishes connecting, which would pull the card out from under support
					// while the customer -- whose path checks only enabled -- still sees it.
					if (availableIntegration.provider === Api.IntegrationProvider.Bloomerang) {
						return !!availableIntegration.enabled;
					}
					return true;
				});
			}
		} else {
			alwaysAvailableIntegrations = alwaysAvailableIntegrations.filter(
				availableIntegration =>
					availableIntegration.provider === Api.IntegrationProvider.MicrosoftDynamics365Sales ||
					availableIntegration.enabled
			);
			availableIntegrations = availableIntegrations.filter(availableIntegration => availableIntegration.enabled);
		}

		return [...availableIntegrations, ...alwaysAvailableIntegrations];
	}, [getExternalIntegrationsQuery.data, impersonationContext, isAdmin, userSession]);

	const enableIntegrationMutation = usePostExternalIntegrationEnableMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Integration status updated successfully');
			invalidateIntegrationsQuery({
				impersonationContext,
			});
		},
	});

	const handleEnableToggle = async (integration: Api.IExternalIntegration) => {
		const enabled = !integration.enabled;

		if (enabled === true) {
			const enableAbility = await userSession.webServiceHelper.callAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `account/integrations/${integration.provider}/enableAbility`,
				}),
				'GET'
			);

			if (enableAbility === Api.EnableAbility.CleanUpRequired) {
				const hasResetAccountPermission = !!userSession?.hasPermission?.(AdminPermission.ResetAccount);
				if (hasResetAccountPermission) {
					setIsAccountCleanUpModalOpen(true);
					setModalIntegrationProvider(integration.provider);
				} else {
					setIsNeedResetModalOpen(true);
				}
			} else if (enableAbility === Api.EnableAbility.CannotEnable) {
				toast.error('There is already an enabled integration on this account.');
			} else {
				enableIntegrationMutation.mutate({
					integrationProvider: integration.provider,
					enabled,
				});
			}
		} else {
			enableIntegrationMutation.mutate({
				integrationProvider: integration.provider,
				enabled,
			});
		}
	};

	const onAccountCleanUpModalConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		// Only attempt the reset call if we have impersonationContext, this currently checked with `isAdmin`
		if (!!result?.isDestructive && !canceled && isAdmin) {
			userSession.webServiceHelper
				.callAsync<Api.IAccount>(
					`admin/account/${impersonationContext.account.id}/reset?includeIntegrations=true`,
					'POST'
				)
				?.then(() => {
					enableIntegrationMutation.mutate({
						integrationProvider: modalIntegrationProvider,
						enabled: true,
					});
				})
				?.catch((error: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(error);
				});
		}

		setIsAccountCleanUpModalOpen(false);
		setModalIntegrationProvider(undefined);
	};

	const getIconUrl = (provider: Api.IntegrationProvider) => {
		switch (provider) {
			case Api.IntegrationProvider.PracticePanther:
				return practicePantherLogo;
			case Api.IntegrationProvider.MicrosoftDynamics365Sales:
				return microsoftDynamics365SalesLogo;
			case Api.IntegrationProvider.MyCase:
				return myCaseLogo;
			case Api.IntegrationProvider.Eclipse:
				return eclipseLogo;
			case Api.IntegrationProvider.Clio:
				return clioLogo;
			case Api.IntegrationProvider.Salesforce:
				return salesforceLogo;
			case Api.IntegrationProvider.ServiceTitan:
				return serviceTitanLogo;
			case Api.IntegrationProvider.QuickBooksOnline:
				return qboLogo;
			case Api.IntegrationProvider.Wealthbox:
				return wealthboxLogo;
			case Api.IntegrationProvider.Redtail:
				return redtailLogo;
			case Api.IntegrationProvider.HousecallPro:
				return housecallProLogo;
			default:
				return '';
		}
	};

	const getStatus = (integration: Api.IExternalIntegration) => {
		if (integration.enabled) {
			if (integration.configuration?.needsConfiguration) {
				return 'Needs Configuration';
			}
			return 'Enabled';
		}
		return 'Disabled';
	};

	const shouldShowApiKeys = () => {
		const account = impersonationContext?.account || regularUserSession?.account?.toJs();
		return account?.features?.publicApi?.enabled;
	};

	const renderApiKeysCard = () => {
		if (!shouldShowApiKeys()) return null;
		if (!matchesSearch('Zapier Keys')) return null;

		const manageUrl = impersonationContext
			? `#/accounts/${impersonationContext.account.id}/api-keys`
			: `/#${match.path}/publicapi`;
		const currentUserSession = impersonationContext ? userSession : regularUserSession;
		const isAdminUser = impersonationContext ? isAdmin : (currentUserSession?.isAdmin ?? false);

		return (
			<IntegrationCard
				key='zapier-keys-integration-card'
				icon={<div className={css(styleSheet.icon)}>Zapier Keys</div>}
				enabled={true}
				canManage={isAdminUser}
				onRenderCtaButton={() => (
					<div className={css(styleSheet.buttonContainer)}>
						{isAdminUser ? (
							<a href={manageUrl} className={css(bs.ctaButtonSmall, bs.noUnderline)}>
								Manage
							</a>
						) : null}
					</div>
				)}
				status={<div className={css(styleSheet.statusText)}>Manage keys to integrate Levitate with Zapier</div>}
			/>
		);
	};

	const renderPersonalApiKeysCard = () => {
		if (!shouldShowApiKeys()) return null;
		// Personal keys are user-owned and have no admin/impersonation variant, so hide the card while impersonating.
		if (impersonationContext) return null;
		if (!matchesSearch('Personal API Keys')) return null;

		const manageUrl = `/#${match.path}/personalapi`;

		return (
			<IntegrationCard
				key='personal-api-keys-integration-card'
				icon={<div className={css(styleSheet.icon)}>Personal API Keys</div>}
				enabled={true}
				canManage={true}
				onRenderCtaButton={() => (
					<div className={css(styleSheet.buttonContainer)}>
						<a href={manageUrl} className={css(bs.ctaButtonSmall, bs.noUnderline)}>
							Manage
						</a>
					</div>
				)}
				status={
					<div className={css(styleSheet.statusText)}>Manage your personal API keys for the Levitate public API</div>
				}
			/>
		);
	};

	const renderOAuthClientsCard = () => {
		if (!shouldShowApiKeys()) return null;
		// OAuth clients are admin-only, account-level, and have no impersonation variant, so hide
		// the card for non-admins and while impersonating.
		if (impersonationContext) return null;
		if (!(regularUserSession?.isAdmin ?? false)) return null;
		if (!matchesSearch('OAuth Clients')) return null;

		const manageUrl = `/#${match.path}/oauthclients`;

		return (
			<IntegrationCard
				key='oauth-clients-integration-card'
				icon={<div className={css(styleSheet.icon)}>OAuth Clients</div>}
				enabled={true}
				canManage={true}
				onRenderCtaButton={() => (
					<div className={css(styleSheet.buttonContainer)}>
						<a href={manageUrl} className={css(bs.ctaButtonSmall, bs.noUnderline)}>
							Manage
						</a>
					</div>
				)}
				status={
					<div className={css(styleSheet.statusText)}>
						Manage OAuth clients that let third-party apps act on behalf of your users
					</div>
				}
			/>
		);
	};

	if (getExternalIntegrationsQuery.isPending) {
		return <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />;
	}

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.cardsGrid)}>
				{renderApiKeysCard()}
				{renderPersonalApiKeysCard()}
				{renderOAuthClientsCard()}
				{externalIntegrations
					?.filter(integration => matchesSearch(getProviderDisplayName(integration.provider)))
					.map(integration => {
						const manageUrl = impersonationContext
							? `#/accounts/${impersonationContext.account.id}/integrations/${integration.provider}`
							: `/#${match.path}/${integration.provider}`;
						const iconUrl = getIconUrl(integration.provider);
						return (
							<IntegrationCard
								key={integration.provider}
								icon={
									iconUrl ? (
										<img src={iconUrl} alt={`${integration.provider} logo`} className={css(styleSheet.icon)} />
									) : (
										<div className={css(styleSheet.icon)}>{getProviderDisplayName(integration.provider)}</div>
									)
								}
								enabled={integration.enabled}
								canManage
								onRenderCtaButton={() => (
									<div className={css(styleSheet.buttonContainer)}>
										{integration.enabled ? (
											<a href={manageUrl} className={css(bs.ctaButtonSmall, bs.noUnderline)}>
												Manage
											</a>
										) : null}
										{isAdmin ? (
											<Button
												variant='secondary'
												size='sm'
												onClick={() => handleEnableToggle(integration)}
												disabled={enableIntegrationMutation.isPending}
											>
												{integration.enabled ? 'Disable' : 'Enable'}
											</Button>
										) : null}
									</div>
								)}
								status={<div>{getStatus(integration)}</div>}
							/>
						);
					})}
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isAccountCleanUpModalOpen,
					onRequestClose: onAccountCleanUpModalConfirmationDialogRequestClose,
				}}
				options={accountCleanUpModalOptions}
				title='There were integrations previously enabled on this account.  To permanently delete all previous data including contacts, companies, tags, households, connection types, text messages, donations, and integration settings, click Delete.  Otherwise, click Cancel and contact Engineering for help.'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isNeedResetModalOpen,
					onRequestClose: () => setIsNeedResetModalOpen(false),
				}}
				options={needAccountResetModalOptions}
				title='There were integrations previously enabled on this account. Please contact Support or Engineering to request cleanup.'
			/>
		</div>
	);
};
